import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { useParams, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { financingRoundsColumns } from "./financingRounds.columns";
import {
  gridFilteredSortedRowIdsSelector,
  gridVisibleColumnFieldsSelector,
} from "@mui/x-data-grid-pro";

const FinancingRoundsDataGrid = forwardRef(({ searchText }, ref) => {
  const { companyId } = useParams();
  const location = useLocation();
  const apiRef = useGridApiRef();
  
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Extract preserved state passed from Portfolio Table
  const dynamicDealId = location.state?.deal_id;

  /* ---------------- FORMATTING UTILITY ---------------- */
  const formatToTwoDecimals = (val) => {
    if (val === null || val === undefined || val === "NA" || val === "") return "—";
    // Remove commas and parse to float
    const cleanStr = String(val).replace(/,/g, "");
    const num = parseFloat(cleanStr);
    return isNaN(num) ? "—" : num.toFixed(2);
  };

  useEffect(() => {
    const fetchFinancingData = async () => {
      // Logic requires a valid dealId to fetch specific history
      if (!dynamicDealId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4200/api';
        
        // Fetch rounds history based on the dealId
        const url = `${API_BASE_URL}/portfolio/financing-rounds?dealId=${dynamicDealId}&partnerCode=${location.state?.partner_doc}&company=${location.state?.company_name}`;
        const response = await fetch(url);
        const result = await response.json();

        if (result.status === "success" && result.data) {
          const transformedRows = result.data.map((item, index) => {
            const invested = formatToTwoDecimals(item.Amount_Invested_Total);
            const preMoney = formatToTwoDecimals(item.Pre_Money_Valuation);
            const postMoney = formatToTwoDecimals(item.Post_Money_Valuation);

            return {
              // Ensure unique ID by combining index and AR_ID if available
              id: `${item.AR_ID || 'round'}-${index}`,
              reportingCurrency: item.Reporting_Currency || "—",
              date: item.Date || "—",
              series: item.Series || "—",
              amountInvested: invested !== "—" ? `${invested}M` : "—",
              preValuation: preMoney !== "—" ? `${preMoney}M` : "—",
              postValuation: postMoney !== "—" ? `${postMoney}M` : "—",
              leadInvestor: item.lead_investor || "—",
            };
          });
          setRows(transformedRows);
        }
      } catch (error) {
        console.error("Error fetching financing rounds:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFinancingData();
  }, [dynamicDealId]);

  /* ---------------- SEARCH ---------------- */
  const filteredRows = useMemo(() => {
    if (!searchText) return rows;
    const q = searchText.toLowerCase().trim();
    return rows.filter((row) =>
      Object.values(row).some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [rows, searchText]);

  /* ---------------- EXPORT ---------------- */
  const exportToExcel = () => {
    if (!apiRef.current) return;
    const rowIds = gridFilteredSortedRowIdsSelector(apiRef);
    if (!rowIds.length) return;
    const visibleFields = gridVisibleColumnFieldsSelector(apiRef);

    const exportData = rowIds.map((id) => {
      const row = apiRef.current.getRow(id);
      const obj = {};
      visibleFields.forEach((field) => {
        const column = financingRoundsColumns.find(col => col.field === field);
        const headerName = column ? column.headerName : field;
        obj[headerName] = row[field];
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Financing Rounds");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer]), `Financing_${companyId}.xlsx`);
  };

  useImperativeHandle(ref, () => ({
    openColumnsPanel: () => apiRef.current?.showPreferences("columns"),
    openFiltersPanel: () => apiRef.current?.showPreferences("filters"),
    exportToExcel,
  }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={420}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 420, width: "100%", bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 2 }}>
      <DataGridPro
        apiRef={apiRef}
        rows={filteredRows}
        columns={financingRoundsColumns}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        initialState={{
          sorting: {
            sortModel: [{ field: "date", sort: "desc" }],
          },
        }}
        sx={{
          "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f9fafb" },
        }}
      />
    </Box>
  );
});

export default FinancingRoundsDataGrid;